// src/ui/StaticMap.jsx — a real map preview with NO API key and no map library.
//
// Static-map endpoints (Google, Mapbox) all want a key, and the app's Google key lives
// in its native config where a browser bundle can't reach it. So this composes the map
// the same way every slippy map does: work out which OSM tiles cover the box, lay them
// out in a grid, and offset the grid so the target coordinate lands dead centre.
// Tiles are plain <img>s — the browser caches them, nothing to initialise, and it
// degrades to an empty tinted box if they fail to load.
import React, { useLayoutEffect, useRef, useState } from 'react';

const TILE = 256;

// 🟢 The pin is inlined rather than imported from ./icons on purpose. It needs a FILLED
//    body with a white outline to read against map tiles, which is the opposite of the
//    shared icon set's stroke-only defaults — so it was overriding them in CSS anyway.
//    Inlining also leaves this module with no local dependencies at all, which matters
//    for a component the bubble AND the picker both mount.
function Pin() {
  return (
    <svg width="30" height="30" viewBox="0 0 24 24" aria-hidden>
      <path
        d="M12 23s9-7 9-13a9 9 0 1 0-18 0c0 6 9 13 9 13Z"
        fill="#FF3B30" stroke="#fff" strokeWidth="1.6" strokeLinejoin="round"
      />
      <circle cx="12" cy="10" r="3.1" fill="#fff" />
    </svg>
  );
}

const lonToX = (lon, z) => ((lon + 180) / 360) * 2 ** z;
const latToY = (lat, z) => {
  const r = (lat * Math.PI) / 180;
  return ((1 - Math.log(Math.tan(r) + 1 / Math.cos(r)) / Math.PI) / 2) * 2 ** z;
};

export default function StaticMap({ lat, lng, zoom = 15, height = 170, width, rounded = true }) {
  // 🟢 MEASURE, don't assume. The box is fluid (it fills a bubble or the picker), and
  //    the tile offsets are computed from its width — guessing it would put the pin
  //    off-centre by however much the guess was wrong.
  const hostRef = useRef(null);
  const [measured, setMeasured] = useState(width || 0);
  useLayoutEffect(() => {
    if (width) return undefined;
    const el = hostRef.current;
    if (!el) return undefined;
    const read = () => setMeasured(el.clientWidth || 0);
    read();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(read);
    ro.observe(el);
    return () => ro.disconnect();
  }, [width]);

  const boxW = measured || width || 320;
  const boxH = height;

  // 🟢 RETINA. OSM serves 256px tiles with no @2x variant, so on a high-DPI laptop the
  //    map renders one map-pixel per CSS pixel and looks soft and blocky next to
  //    everything else in the app. Fetching ONE ZOOM LEVEL DEEPER and drawing each tile
  //    at half size covers the identical ground with twice the pixel density — the
  //    standard trick, and the reason this looked cheap on a desktop screen.
  const dpr = typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1;
  const hi = dpr > 1.3;
  const z = Math.min(19, Math.round(zoom) + (hi ? 1 : 0));
  const T = hi ? TILE / 2 : TILE;   // CSS px per tile

  const n = 2 ** z;
  const cx = lonToX(lng, z) * T;   // centre, in CSS pixels
  const cy = latToY(lat, z) * T;
  const left = cx - boxW / 2;
  const top = cy - boxH / 2;

  const x0 = Math.floor(left / T);
  const y0 = Math.floor(top / T);
  const x1 = Math.floor((left + boxW) / T);
  const y1 = Math.floor((top + boxH) / T);

  const tiles = [];
  for (let ty = y0; ty <= y1; ty++) {
    for (let tx = x0; tx <= x1; tx++) {
      const wrapX = ((tx % n) + n) % n;          // wrap the antimeridian
      if (ty < 0 || ty >= n) continue;            // clamp the poles
      tiles.push({
        key: `${tx}_${ty}`,
        src: `https://tile.openstreetmap.org/${z}/${wrapX}/${ty}.png`,
        x: tx * T - left,
        y: ty * T - top,
        size: T,
      });
    }
  }

  return (
    <div ref={hostRef} className={`static-map ${rounded ? 'rounded' : ''}`} style={{ height: boxH, ...(width ? { width } : null) }}>
      <div className="sm-tiles">
        {tiles.map((t) => (
          <img
            key={t.key}
            src={t.src}
            alt=""
            width={t.size}
            height={t.size}
            loading="lazy"
            style={{ left: t.x, top: t.y, width: t.size, height: t.size }}
            onError={(e) => { e.currentTarget.style.visibility = 'hidden'; }}
          />
        ))}
      </div>
      <span className="sm-pin"><Pin /></span>
      <span className="sm-credit">© OpenStreetMap</span>
    </div>
  );
}
